import React from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, FileText, Book } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const TakingInvestmentSellingPractice = () => {
  const [, navigate] = useLocation();
  
  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-blue-600"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
        </div>
        
        <div>
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/mentorship">Mentorship</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <span className="text-gray-600">Taking investment and selling your practice [Guest Lecture]</span>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
            Taking investment and selling your practice [Guest Lecture]
          </h1>
          <p className="text-[#7F8C8D] mb-6">
            Expert insights on practice valuation, investment strategies, and exit planning for longevity medicine practices.
          </p>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <FileText className="h-4 w-4 mr-1" />
              <span>Guest Lecture</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <Book className="h-4 w-4 mr-1" />
              <span>Last Updated: January 11, 2025</span>
            </div>
          </div>
          
          <Separator />
          
          {/* Video Content */}
          <div className="mt-8">
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Guest Lecture Video</h2>
              <p className="text-gray-700 mb-6">
                This guest lecture provides comprehensive guidance on practice investment opportunities, 
                valuation methodologies, and strategic exit planning specifically for longevity medicine practices.
              </p>
              
              {/* Video Embed */}
              <div className="flex justify-center">
                <iframe 
                  src="https://share.descript.com/embed/jrmUKEUmQnl" 
                  width="640" 
                  height="360" 
                  frameBorder="0" 
                  allowFullScreen
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
            
            {/* Additional Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-3">Key Topics Covered</h3>
                <ul className="space-y-2 text-blue-800">
                  <li>• Practice valuation methodologies</li>
                  <li>• Investment readiness assessment</li>
                  <li>• Due diligence processes</li>
                  <li>• Exit strategy planning</li>
                  <li>• Negotiation best practices</li>
                  <li>• Legal and financial considerations</li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="font-semibold text-green-900 mb-3">Learning Objectives</h3>
                <ul className="space-y-2 text-green-800">
                  <li>• Understand practice valuation frameworks</li>
                  <li>• Identify investment opportunities and risks</li>
                  <li>• Develop strategic exit planning</li>
                  <li>• Navigate transaction processes</li>
                  <li>• Optimize deal structures</li>
                  <li>• Ensure regulatory compliance</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-amber-800">Guest Lecture Format</h3>
                  <p className="mt-2 text-amber-700">
                    This session features industry experts sharing real-world insights and practical strategies 
                    for practice owners considering investment or exit opportunities in the longevity medicine space.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
};

export default TakingInvestmentSellingPractice;